import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const faqData = [
  {
    category: "Booking & Payments",
    questions: [
      {
        question: "How do I secure my spot on a trip?",
        answer: "Simply pay the 5% deposit at checkout! Once we receive your deposit, we'll send a confirmation email and start locking in your local arrangements. Your package price is guaranteed once the deposit is paid."
      },
      {
        question: "When do I need to pay the rest of my balance?",
        answer: "The remaining 95% balance is due 60 days before your trip begins. We'll send you a reminder email so you don't miss the deadline. If your trip is less than 60 days away at the time of booking, full payment is required."
      },
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards (Visa, Mastercard, American Express) through our secure Stripe payment system. Your payment information is encrypted and never stored on our servers."
      },
      {
        question: "Is my deposit refundable?",
        answer: "The 5% deposit is non-refundable as it covers initial administrative costs and vendor deposits. However, the remaining balance has different refund terms based on when you cancel. See our Terms & Conditions for full details."
      }
    ]
  },
  {
    category: "Cancellations & Refunds",
    questions: [
      {
        question: "What happens if I need to cancel?",
        answer: "Our cancellation policy is based on how far in advance you cancel:\n\n• 60+ days out: Full refund of your balance (excluding the 5% deposit)\n• 30–59 days out: 50% refund of the total package price\n• Less than 30 days: No refund available\n\nAll cancellation requests must be submitted in writing to hello@chimbatravel.com."
      },
      {
        question: "How long does it take to receive a refund?",
        answer: "Approved refunds are processed within 7–10 business days and will be returned to your original payment method. You'll receive an email confirmation once the refund has been processed."
      },
      {
        question: "What if Chimba Travel cancels my trip?",
        answer: "If we need to cancel a tour due to low enrollment or circumstances beyond our control (Force Majeure), you'll receive a full refund or credit toward a future trip—your choice!"
      }
    ]
  },
  {
    category: "Travel Documents & Requirements",
    questions: [
      {
        question: "What documents do I need to travel?",
        answer: "You'll need:\n\n• A valid passport with at least 6 months validity beyond your return date\n• Required visas for your destination countries\n• Any necessary vaccinations or health certificates\n\nWe recommend checking specific requirements for your destination well in advance of your trip."
      },
      {
        question: "Do I need travel insurance?",
        answer: "While not mandatory, we highly recommend comprehensive travel insurance to protect your investment. Since the 5% deposit is non-refundable, insurance can provide peace of mind for unexpected cancellations, medical emergencies, lost luggage, and more."
      },
      {
        question: "What if I have dietary restrictions or medical conditions?",
        answer: "Please notify us of any dietary restrictions, allergies, or medical conditions at the time of booking. We'll do our best to accommodate your needs and inform our local partners to ensure a safe and enjoyable experience."
      }
    ]
  },
  {
    category: "During Your Trip",
    questions: [
      {
        question: "What's included in the package price?",
        answer: "Each package clearly lists what's included on its detail page. Typically, this includes accommodations, guided tours, transportation between destinations, and specified meals. Check the 'What's Included' section of your chosen package for specifics."
      },
      {
        question: "Will I have free time during the trip?",
        answer: "Yes! Our itineraries balance guided activities with free time so you can explore at your own pace, relax, or discover hidden gems on your own. Your trip leader will provide recommendations for making the most of your free time."
      },
      {
        question: "What if I have an emergency during my trip?",
        answer: "You'll have 24/7 access to our emergency support line. Your trip leader will also be available to assist with any issues that arise. We recommend saving our emergency contact information and keeping a copy of your travel documents separate from the originals."
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-maroon/10 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-4 flex items-center justify-between text-left hover:bg-sand/20 transition-colors px-2 rounded-sm"
        data-testid={`faq-question-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="font-semibold text-maroon pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="text-terracotta flex-shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-maroon/60 flex-shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 px-2 text-maroon/80 whitespace-pre-line">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="py-16" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center space-x-2 text-maroon hover:text-terracotta mb-8">
          <ArrowLeft size={20} />
          <span className="font-semibold">Back to Home</span>
        </Link>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-terracotta/10 rounded-full mb-4">
            <HelpCircle className="text-terracotta" size={40} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-maroon/70">
            Find answers to common questions about booking with Chimba Travel
          </p>
        </div>

        {/* Quick Links */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {faqData.map((category, idx) => (
            <a
              key={idx}
              href={`#${category.category.toLowerCase().replace(/\s+/g, '-')}`}
              className="px-4 py-2 bg-sand/50 hover:bg-terracotta hover:text-white rounded-full text-sm font-semibold text-maroon transition-colors"
            >
              {category.category}
            </a>
          ))}
        </div>

        {/* FAQ Sections */}
        {faqData.map((category, categoryIndex) => (
          <div 
            key={categoryIndex} 
            className="card mb-8"
            id={category.category.toLowerCase().replace(/\s+/g, '-')}
          >
            <h2 className="text-2xl font-heading font-bold text-maroon mb-6 pb-4 border-b-2 border-maroon/10">
              {category.category}
            </h2>
            <div>
              {category.questions.map((item, questionIndex) => (
                <FAQItem
                  key={questionIndex}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                  onClick={() => toggleItem(categoryIndex, questionIndex)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Still have questions */}
        <div className="card bg-maroon text-white text-center">
          <h3 className="text-2xl font-heading font-bold mb-4">Still Have Questions?</h3>
          <p className="mb-6 opacity-90">
            Our travel specialists are here to help you plan your perfect adventure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation" className="btn-secondary">
              Book a Free Consultation
            </Link>
            <a 
              href="mailto:hello@chimbatravel.com" 
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-sm font-semibold transition-colors"
            >
              Email Us
            </a>
          </div>
        </div>

        {/* Links to Terms */}
        <div className="mt-8 text-center">
          <p className="text-maroon/70">
            For complete policy details, please review our{' '}
            <Link to="/terms" className="text-maroon hover:text-terracotta underline font-semibold">
              Terms & Conditions
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
